// Intake form schemas - each form has sections with fields
// type: "form" (default) for fillable forms, "instruction" for read-only instruction pages
export const INTAKE_FORMS = {
  meta_facebook: {
    id: "meta_facebook",
    name: "Facebook / Meta Campaign Setup",
    description: "Step-by-step instructions for granting access to your Facebook / Meta Business assets",
    icon: "target",
    type: "instruction",
    instruction: {
      intro: "Before we can run Facebook / Meta ad campaigns on your behalf, we need access to your Facebook Page, Instagram, and Ad Account. The full step-by-step walkthrough is in the document below — please review it carefully and grant the access listed.",
      external_url: "https://docs.google.com/document/d/1sWLywmRoG-5xWTnbqWxeEGM9a0QpU8L1ZkQmRa8iPek/edit",
      steps: [
        "Open the Facebook / Meta Access Setup Guide using the button below.",
        "Follow the instructions to add Ready Prospect as an admin / partner on your Facebook Page, Instagram, and Ad Account.",
        "Once access is granted, reply to your sales rep to confirm — we'll verify and kick off your campaign setup.",
      ],
    },
    sections: [],
  },

  seo_aeo: {
    id: "seo_aeo",
    name: "SEO & AEO Intake Form",
    description: "Search & AI visibility discovery questionnaire",
    icon: "search",
    intro: "This helps us understand your business, your priorities, and where you stand today so we can build an optimization plan that targets the work most likely to move revenue. Answer what you can—estimates are fine, and we'll fill gaps together on our kickoff call.",
    sections: [
      {
        title: "Basics",
        fields: [
          { id: "business_name", label: "Business name", type: "text", required: true },
          { id: "form_date", label: "Date", type: "text", placeholder: "MM/DD/YYYY", required: true },
          { id: "website_url", label: "Website", type: "text", placeholder: "https://", required: true },
          { id: "primary_contact", label: "Primary contact", type: "text", required: true },
        ],
      },
      {
        title: "Section 1 · Business & Goals",
        fields: [
          { id: "business_description", label: "In one or two sentences, what does your business do and who do you serve?", type: "textarea", required: true },
          { id: "top_goals", label: "What are your top business goals for the next 6–12 months?", type: "textarea", placeholder: "Rank them if there's more than one (e.g., more booked jobs, higher-value clients, new service area).", required: true },
          { id: "success_criteria", label: "What would make this engagement a clear win for you, and how will you measure it?", type: "textarea", placeholder: "Calls, form fills, booked appointments, revenue, ranking for specific terms—whatever matters most.", required: true },
        ],
      },
      {
        title: "Section 2 · What We're Optimizing",
        fields: [
          { id: "services_to_optimize", label: "List the specific products or services you want us to focus on optimizing.", type: "textarea", required: true },
          {
            id: "profit_center_ranking",
            label: "Rank those offerings by profit center—most profitable / important at the top.",
            type: "ranking_table",
            helper: "We weight optimization effort toward what actually pays the bills, not just what gets clicks.",
            rows: 5,
            columns: [
              { key: "product", label: "Product / Service", placeholder: "What you sell", type: "text" },
              { key: "reason", label: "Why it ranks here (margin, volume, growth)", placeholder: "Margin / volume / strategic", type: "textarea" },
            ],
          },
          { id: "growth_phaseout", label: "Are there any services you want to grow, phase out, or keep off our radar?", type: "textarea" },
          { id: "geographic_areas", label: "Which geographic areas do you serve, and is there one you most want to grow?", type: "textarea", required: true },
        ],
      },
      {
        title: "Section 3 · Current Digital Presence",
        fields: [
          { id: "gbp_status", label: "Have you claimed and verified your Google Business Profile?", type: "select", options: ["Yes – claimed & verified", "Claimed, not verified", "No / Not sure"], required: true },
          { id: "gbp_link", label: "If yes, link or listing name", type: "text", placeholder: "Paste the Google Maps / GBP link" },
          { id: "social_profiles", label: "Paste links to your active social media profiles", type: "textarea", placeholder: "Facebook, Instagram, LinkedIn, YouTube, TikTok, X, etc.—whatever you actually use." },
          { id: "platform_and_tools", label: "What website platform and key tools are you running?", type: "textarea", placeholder: "e.g., WordPress, Wix, Squarespace; plus CRM, booking, or call-tracking tools if any." },
        ],
      },
      {
        title: "Section 4 · Competitive Landscape",
        fields: [
          { id: "competitors", label: "List three competitors you want to outrank or benchmark against", type: "textarea", placeholder: "Name + website for each", required: true },
          { id: "competitive_gap", label: "What do you think those competitors do better than you online—and where do you beat them?", type: "textarea" },
        ],
      },
      {
        title: "Section 5 · Existing Materials & Access",
        fields: [
          { id: "audit_status", label: "Do you have any existing SEO/AEO audits, analytics, or reports we should review?", type: "select", options: ["Attaching now", "Can send later", "None available"] },
          { id: "audit_file", label: "Attach audit / analytics / report (PDF, DOCX, XLSX, ZIP, etc.)", type: "file", helper: "Feel free to attach an existing audit, Search Console export, or prior agency work.", condition: { field: "audit_status", value: "Attaching now" } },
          { id: "approval_authority", label: "Who has authority to approve website changes, and is anyone else we should loop in?", type: "textarea", required: true },
        ],
      },
    ],
  },

  a2p_registration: {
    id: "a2p_registration",
    name: "ProspectMax Intake Form",
    description: "Required intake for ProspectMax packages, AI Voice Agents, and SMS communications registration",
    icon: "phone",
    intro:
      "The following information will be used to route your leads to the right person at your organization. You will also see that we need to collect some business information to get your new phone number registered properly with the phone carriers so that we can text your potential customers and set up the AI voice agent if you have selected that option.",
    sections: [
      {
        title: "Lead Routing & CRM Access",
        fields: [
          { id: "lead_alert_email", label: "What email should we send the new lead alerts to?", type: "text", required: true, placeholder: "leads@yourcompany.com" },
          { id: "lead_alert_phone", label: "What phone number should we text new lead alerts to?", type: "text", required: true, placeholder: "(555) 123-4567" },
          { id: "use_crm", label: "Do you wish to track and manage your leads using our CRM?", type: "select", options: ["Yes", "No"], required: true },
          { id: "crm_user_1_name", label: "CRM User #1 — Full Name", type: "text", placeholder: "First and last name", condition: { field: "use_crm", value: "Yes" } },
          { id: "crm_user_1_email", label: "CRM User #1 — Email", type: "text", placeholder: "user1@yourcompany.com", condition: { field: "use_crm", value: "Yes" } },
          { id: "crm_user_2_name", label: "CRM User #2 — Full Name (optional)", type: "text", placeholder: "First and last name", condition: { field: "use_crm", value: "Yes" } },
          { id: "crm_user_2_email", label: "CRM User #2 — Email (optional)", type: "text", placeholder: "user2@yourcompany.com", condition: { field: "use_crm", value: "Yes" } },
        ],
      },
      {
        title: "Business / Organization Information",
        fields: [
          { id: "legal_name", label: "Legal Business Name", type: "text", required: true, placeholder: "Must match government registration exactly" },
          { id: "dba_name", label: "Doing Business As (DBA) Name", type: "text", placeholder: "If applicable" },
          { id: "business_type", label: "Business Type", type: "select", options: ["Corporation", "LLC", "Partnership", "Sole Proprietorship", "Non-Profit", "Government Entity"], required: true },
          { id: "industry", label: "Industry / Vertical", type: "text", required: true, placeholder: "e.g., Healthcare, Real Estate, Marketing" },
          { id: "ein", label: "Business Registration Number (EIN)", type: "text", required: true, placeholder: "Employer Identification Number" },
          { id: "country", label: "Country of Registration", type: "select", options: ["United States", "Other"], required: true },
          { id: "website", label: "Business Website", type: "text", required: true, placeholder: "Must be live and reflect business activity" },
        ],
      },
      {
        title: "Authorized Contact Person",
        fields: [
          { id: "contact_name", label: "Full Name", type: "text", required: true },
          { id: "contact_title", label: "Job Title / Role", type: "text", required: true },
          { id: "contact_email", label: "Email Address", type: "text", required: true },
          { id: "contact_phone", label: "Phone Number", type: "text", required: true, placeholder: "Must be valid and reachable" },
        ],
      },
      {
        title: "Business Address",
        fields: [
          { id: "street_address", label: "Physical Street Address", type: "text", required: true, placeholder: "No P.O. Boxes - must be physical address" },
          { id: "city", label: "City", type: "text", required: true },
          { id: "state", label: "State", type: "text", required: true },
          { id: "zip", label: "ZIP / Postal Code", type: "text", required: true },
          { id: "address_country", label: "Country", type: "text", required: true },
        ],
      },
      {
        title: "Use Case Details",
        fields: [
          { id: "use_case", label: "Describe your specific use case for SMS/Voice", type: "textarea", required: true, placeholder: "Be specific - e.g., 'home services appointment reminders' not just 'marketing'" },
          { id: "message_volume", label: "Estimated monthly message volume", type: "text", placeholder: "e.g., 5,000 messages/month" },
          { id: "opt_in_method", label: "How do customers opt-in to receive messages?", type: "textarea", placeholder: "Describe your consent collection process" },
        ],
      },
    ],
  },

  web_design: {
    id: "web_design",
    name: "Website Questionnaire Form",
    description: "Collect information for website design and development",
    icon: "globe",
    sections: [
      {
        title: "Business Information",
        fields: [
          { id: "business_name", label: "Business / Brand Name", type: "text", required: true },
          { id: "phone", label: "Phone", type: "text", required: true },
          { id: "email", label: "Email", type: "text", required: true },
          { id: "website_url", label: "Website URL", type: "text", placeholder: "Current website if any" },
        ],
      },
      {
        title: "Project Type",
        fields: [
          { id: "website_type", label: "Type of Website", type: "select", options: ["New Website", "Redesign", "Migration (e.g., platform change)"], required: true },
        ],
      },
      {
        title: "Business & Goals",
        fields: [
          { id: "business_description", label: "Business Description / About the Company", type: "textarea", required: true },
          { id: "target_audience", label: "Target Audience", type: "textarea", required: true },
          { id: "main_goals", label: "Main Goals of the Website (e.g., leads, sales, bookings, branding)", type: "textarea", required: true },
        ],
      },
      {
        title: "Required Pages",
        fields: [
          { id: "page_1", label: "Page 1", type: "text", placeholder: "e.g., Home" },
          { id: "page_2", label: "Page 2", type: "text", placeholder: "e.g., About" },
          { id: "page_3", label: "Page 3", type: "text", placeholder: "e.g., Services" },
          { id: "page_4", label: "Page 4", type: "text", placeholder: "e.g., Contact" },
          { id: "page_5", label: "Page 5", type: "text", placeholder: "e.g., Blog" },
          { id: "page_6", label: "Page 6", type: "text" },
          { id: "page_7", label: "Page 7", type: "text" },
          { id: "page_8", label: "Page 8", type: "text" },
        ],
      },
      {
        title: "Design & Content",
        fields: [
          { id: "design_preferences", label: "Design Preferences (colors, style, inspiration websites)", type: "textarea", required: true },
          { id: "content_status", label: "Content Status", type: "select", options: ["Content ready", "Needs help with content"], required: true },
        ],
      },
      {
        title: "Functional Requirements",
        fields: [
          { id: "functional_requirements", label: "Functional Requirements", type: "checkboxes", options: ["Forms", "Booking system", "Payment integration", "CRM / Email integration"], required: true },
          { id: "domain_hosting", label: "Domain & Hosting Access Available?", type: "select", options: ["Yes", "No"], required: true },
        ],
      },
      {
        title: "Timeline & Notes",
        fields: [
          { id: "deadline", label: "Deadline / Timeline", type: "text", required: true, placeholder: "e.g., 4 weeks, by June 1st" },
          { id: "additional_notes", label: "Additional Notes or Special Requirements", type: "textarea" },
        ],
      },
    ],
  },
};

export const getFormById = (formId) => INTAKE_FORMS[formId] || null;
export const getAllForms = () => Object.values(INTAKE_FORMS);
